/**
 * Shared mount helper for the imperative admin surfaces (LLM / users /
 * signups / audit).
 *
 * Each admin view renders a static skeleton, then an `onMount` hook locates
 * its root by `data-*-root` selector and fans out the data fetch + overlay.
 * The naive form queried the document SYNCHRONOUSLY inside the `onMount`
 * callback — but under the @llui signal runtime the callback can fire before
 * the returned subtree is connected to the document, so `querySelector`
 * returns null and the whole fan-out (incl. the non-admin not-found overlay)
 * silently bails (#198: `06` 119.A / `30` 30.A flaked red; non-admins saw an
 * empty admin shell instead of the denial). This helper defers the body
 * across animation frames until the root is actually connected, then runs it
 * exactly once and threads the disposer for navigate-away cleanup.
 *
 * (The durable cure is to migrate these views to the state-driven pattern —
 * Model + Effect + reactive view — like the account page (#152) and settings;
 * tracked in #199. This helper removes the race in the meantime without a
 * rewrite.)
 */
import { onMount, type Mountable } from '@llui/dom';

/** Max frames to wait for the root to connect before giving up (~1s @ 60fps). */
const MAX_FRAMES = 60;

/**
 * Run `fn(root)` once the element matching `rootSelector` is connected to the
 * document, polling across animation frames. `fn` may return a disposer that
 * runs on unmount. Returns the `onMount` Mountable — place it in the view tree.
 */
export function onAdminMount(
  rootSelector: string,
  fn: (root: Element) => (() => void) | void,
): Mountable {
  return onMount(() => {
    let dispose: (() => void) | void;
    let cancelled = false;
    let raf = 0;

    const attempt = (framesLeft: number): void => {
      if (cancelled) return;
      const root = document.querySelector(rootSelector);
      if (root && root.isConnected) {
        dispose = fn(root);
        return;
      }
      if (framesLeft <= 0) return;
      raf = requestAnimationFrame(() => attempt(framesLeft - 1));
    };
    attempt(MAX_FRAMES);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (dispose) dispose();
    };
  });
}
